import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Rocket, Target, Compass, Heart, Sparkles, Trophy, Flag, TrendingUp, TrendingDown,
  Users, UserPlus, Database, GraduationCap, MapPin, Plus, Trash2, Pencil, Check, X,
  ChevronDown, Download, RotateCcw, Search, Award, Gem, Star, Zap, CheckCircle2,
  Building2, ListChecks, ClipboardCheck, ShieldCheck, Layers, Gauge,
  Paperclip, Clock, AlertTriangle, FileText, Upload, Loader2,
  ChevronLeft, ChevronRight, StickyNote, Quote, CalendarDays,
  Copy, Eraser, GitCompareArrows, HeartPulse,
} from 'lucide-react';
import api from '@/lib/api';
import { useGet } from '@/lib/useData';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

// --------------------------------------------------------------------------- //
//  Constantes                                                                  //
// --------------------------------------------------------------------------- //
const STATUTS = [
  { value: 'a_lancer', label: 'À lancer', short: 'à lancer', color: 'var(--status-planned)' },
  { value: 'en_cours', label: 'En cours', short: 'en cours', color: 'var(--status-progress)' },
  { value: 'teste', label: 'Testé', short: 'testé', color: 'var(--status-tested)' },
  { value: 'deploye', label: 'Déployé', short: 'déployé', color: 'var(--status-deployed)' },
  { value: 'abandonne', label: 'Abandonné', short: 'abandonné', color: 'var(--status-dropped)' },
];
const STATUT_MAP = Object.fromEntries(STATUTS.map((s) => [s.value, s]));
const EFFORTS = [{ value: 'faible', label: 'Faible', n: 1 }, { value: 'moyen', label: 'Moyen', n: 2 }, { value: 'eleve', label: 'Élevé', n: 3 }];
const EFFORT_MAP = Object.fromEntries(EFFORTS.map((e) => [e.value, e]));
const PRIORITES = ['P1', 'P2', 'P3'];
const WS_ICON = { 1: Users, 2: UserPlus, 3: MapPin, 4: Database, 5: GraduationCap, 6: Heart };
const wsColor = (n) => `var(--workstream-${((n - 1) % 6) + 1})`;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

// --------------------------------------------------------------------------- //
//  Micro-composants                                                            //
// --------------------------------------------------------------------------- //
function EditableText({ value, onSave, placeholder = '—', className = '', inputClassName = '', multiline = false, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  if (editing) {
    const commit = () => { setEditing(false); if ((draft ?? '') !== (value ?? '')) onSave(draft); };
    const Cmp = multiline ? Textarea : Input;
    return (
      <Cmp autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        className={`rounded-lg ${inputClassName}`} data-testid={testid ? `${testid}-input` : undefined} />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid={testid}
      className={`group/edit inline-flex w-full items-start gap-1 rounded text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${className}`}>
      <span className={(value === '' || value == null) ? 'italic text-muted-foreground' : ''}>{value || placeholder}</span>
      <Pencil className="mt-1 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/edit:opacity-50" />
    </button>
  );
}

function EditableList({ items, onChange, addLabel = 'Ajouter', dotColor, testid }) {
  const arr = items || [];
  const setItem = (i, v) => { const c = [...arr]; c[i] = v; onChange(c); };
  const del = (i) => onChange(arr.filter((_, j) => j !== i));
  const add = () => onChange([...arr, 'Nouvel élément']);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {arr.map((it, i) => (
        <div key={i} className="group/li flex items-start gap-1.5">
          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dotColor || 'var(--tenant-accent-500)' }} />
          <EditableText value={it} onSave={(v) => setItem(i, v)} className="flex-1 text-sm" inputClassName="h-7 text-sm" testid={testid ? `${testid}-item-${i}` : undefined} />
          <button type="button" onClick={() => del(i)} aria-label="Supprimer" className="mt-0.5 opacity-0 transition-opacity group-hover/li:opacity-100">
            <X className="h-3.5 w-3.5 text-rose-500" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1 text-[11px] font-medium accent-text hover:underline" data-testid={testid ? `${testid}-add` : undefined}>
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

function EditableValues({ values, onChange, testid }) {
  const arr = values || [];
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState('');
  const startEdit = (i) => { setEditIdx(i); setDraft(arr[i] ?? ''); };
  const commit = () => {
    if (editIdx == null) return;
    const c = [...arr];
    const v = (draft ?? '').trim();
    if (v) c[editIdx] = v; else c.splice(editIdx, 1);
    setEditIdx(null);
    onChange(c);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    const c = [...arr];
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };
  const del = (i) => onChange(arr.filter((_, j) => j !== i));
  const add = () => onChange([...arr, 'Nouvelle valeur']);
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={testid}>
      {arr.map((v, i) => (
        <div key={i} className="group/val inline-flex items-center gap-0.5 rounded-full bg-[color:var(--tenant-accent-050)] py-0.5 pl-0.5 pr-1 text-xs font-medium text-[color:var(--tenant-accent-600)]">
          <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Déplacer à gauche"
            className="rounded-full p-0.5 opacity-40 transition-opacity enabled:hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-10" data-testid={testid ? `${testid}-left-${i}` : undefined}><ChevronLeft className="h-3 w-3" /></button>
          {editIdx === i ? (
            <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setEditIdx(null); } }}
              className="h-6 w-28 rounded-md px-2 text-xs" data-testid={testid ? `${testid}-input-${i}` : undefined} />
          ) : (
            <button type="button" onClick={() => startEdit(i)} className="rounded px-1 hover:underline" title="Cliquer pour modifier" data-testid={testid ? `${testid}-item-${i}` : undefined}>{v}</button>
          )}
          <button type="button" disabled={i === arr.length - 1} onClick={() => move(i, 1)} aria-label="Déplacer à droite"
            className="rounded-full p-0.5 opacity-40 transition-opacity enabled:hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-10" data-testid={testid ? `${testid}-right-${i}` : undefined}><ChevronRight className="h-3 w-3" /></button>
          <button type="button" onClick={() => del(i)} aria-label="Supprimer" className="rounded-full p-0.5 text-rose-500 opacity-0 transition-opacity group-hover/val:opacity-100" data-testid={testid ? `${testid}-del-${i}` : undefined}><X className="h-3 w-3" /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground" data-testid={testid ? `${testid}-add` : 'plan-valeur-add'}><Plus className="h-3 w-3" /> Valeur</button>
    </div>
  );
}

function StatusSelect({ value, onChange, testid }) {
  const s = STATUT_MAP[value] || STATUT_MAP.a_lancer;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[132px] rounded-lg text-xs" data-testid={testid}>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        {STATUTS.map((st) => (
          <SelectItem key={st.value} value={st.value} className="text-xs">
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: st.color }} /> {st.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EffortDots({ effort }) {
  const n = (EFFORT_MAP[effort] || EFFORT_MAP.moyen).n;
  return (
    <span className="inline-flex items-center gap-0.5" title={`Effort ${(EFFORT_MAP[effort] || {}).label}`}>
      {[0, 1, 2].map((i) => (
        <span key={i} className={`h-1.5 w-1.5 rounded-full ${i < n ? 'bg-foreground/70' : 'bg-foreground/20'}`} />
      ))}
    </span>
  );
}

function ProgressRing({ pct = 0, size = 128, stroke = 12 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--tenant-accent-500)" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.2,0.8,0.2,1)' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-bold tabular" data-testid="global-progress-percent">{pct}%</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">déployé</span>
      </div>
    </div>
  );
}

// Donut multi-segments : un arc par statut, cliquable pour filtrer la liste.
function StatusDonut({ repartition = {}, total = 0, deployedPct = 0, selected = 'all', onSelect, size = 132, stroke = 14 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const gap = total > 0 ? 2 : 0;
  let acc = 0;
  const arcs = [];
  for (const s of STATUTS) {
    const n = repartition[s.value] || 0;
    if (n <= 0) continue;
    const len = (n / total) * c;
    arcs.push({ ...s, n, len, offset: -acc });
    acc += len;
  }
  const selMeta = selected !== 'all' ? STATUT_MAP[selected] : null;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="plan-donut">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
        {arcs.map((seg) => {
          const dim = selected !== 'all' && selected !== seg.value;
          const isSel = selected === seg.value;
          const dash = Math.max(seg.len - gap, 0.5);
          return (
            <circle key={seg.value} cx={size / 2} cy={size / 2} r={r}
              stroke={seg.color} strokeWidth={isSel ? stroke + 4 : stroke} fill="none"
              strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={seg.offset}
              className="cursor-pointer transition-[stroke-width,opacity] duration-200"
              style={{ opacity: dim ? 0.25 : 1 }}
              onClick={() => onSelect(seg.value)}
              data-testid={`plan-donut-seg-${seg.value}`}>
              <title>{`${seg.label} : ${seg.n} (${Math.round((seg.n / total) * 100)}%)`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
        {selMeta ? (
          <>
            <span className="font-display text-3xl font-bold tabular" style={{ color: selMeta.color }} data-testid="donut-center-count">{repartition[selected] || 0}</span>
            <span className="max-w-[92px] text-[10px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">{selMeta.label}</span>
            <button type="button" onClick={() => onSelect('all')} className="pointer-events-auto mt-0.5 text-[9px] text-muted-foreground underline hover:text-foreground" data-testid="donut-clear">tout voir</button>
          </>
        ) : (
          <>
            <span className="font-display text-3xl font-bold tabular" data-testid="global-progress-percent">{deployedPct}%</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">déployé</span>
            <span className="mt-0.5 text-[9px] text-muted-foreground tabular">{total} actions</span>
          </>
        )}
      </div>
    </div>
  );
}


const BADGE_META = {
  bronze: { label: 'Bronze', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  argent: { label: 'Argent', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  or: { label: 'Or', cls: 'bg-yellow-100 text-yellow-800 border-yellow-400' },
};

function Confetti({ show }) {
  if (!show) return null;
  const pieces = Array.from({ length: 16 });
  const colors = ['#EE722E', '#3B82F6', '#10B981', '#F59E0B', '#E11D48', '#7C3AED'];
  return (
    <div className="pointer-events-none fixed left-1/2 top-24 z-[300] -translate-x-1/2" aria-hidden>
      {pieces.map((_, i) => (
        <span key={i} className="exec-confetti-piece absolute block h-2 w-2 rounded-sm"
          style={{ left: `${(i - 8) * 12}px`, background: colors[i % colors.length], animationDelay: `${(i % 5) * 40}ms` }} />
      ))}
    </div>
  );
}

// Mini répartition par statut (barre segmentée + compteurs) pour l'en-tête d'un chantier.
function MiniStatusBar({ repartition = {}, total = 0, onSelect, testid }) {
  const segs = STATUTS.map((s) => ({ ...s, n: repartition[s.value] || 0 }));
  return (
    <div data-testid={testid}>
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
        {segs.map((s) => {
          const w = total ? (s.n / total) * 100 : 0;
          if (!w) return null;
          return <div key={s.value} title={`${s.label} : ${s.n}`} style={{ width: `${w}%`, background: s.color }} className="h-full" />;
        })}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
        {segs.filter((s) => s.n > 0).map((s) => (
          <button key={s.value} type="button" onClick={onSelect ? () => onSelect(s.value) : undefined}
            title={onSelect ? `Filtrer : ${s.label}` : `${s.label} : ${s.n}`}
            className={`flex items-center gap-1 text-[10px] ${onSelect ? 'cursor-pointer hover:opacity-70' : 'cursor-default'}`}
            data-testid={testid ? `${testid}-${s.value}` : undefined}>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
            <span className="tabular font-semibold text-foreground">{s.n}</span>
            <span className="text-muted-foreground">{s.short}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


// --------------------------------------------------------------------------- //
//  Page                                                                        //
// --------------------------------------------------------------------------- //
export default function PlanStrategique() {
  const [editionId, setEditionId] = useState(null);
  const q = useGet('/strategy/plan', editionId ? { plan_id: editionId } : undefined, true);
  const qc = useQueryClient();
  const d = q.data;
  // Contexte de l'édition (année) courante — sert à scoper toutes les mutations.
  const curPid = d?.edition?.id || null;
  const qp = curPid ? `?plan_id=${curPid}` : '';
  const [busy, setBusy] = useState(false);
  const [kpiOverride, setKpiOverride] = useState({});
  const [deleteYearOpen, setDeleteYearOpen] = useState(false);
  const [newYear, setNewYear] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const attrMeasuresQ = useGet('/attrition/measures', undefined, true);
  const attrOptions = ((attrMeasuresQ.data && attrMeasuresQ.data.catalog) || [])
    .filter((c) => c.value !== null && c.value !== undefined)
    .map((c) => ({ value: `${c.association}::${c.key}`, label: c.label, unit: c.unit, association: c.association }));
  const [celebrate, setCelebrate] = useState(false);
  const [search, setSearch] = useState('');
  const [fStatut, setFStatut] = useState('all');
  const [fResp, setFResp] = useState('all');
  const [fPreuve, setFPreuve] = useState('all');
  const [fRetard, setFRetard] = useState(false);
  const [openWs, setOpenWs] = useState({});
  const [openAtt, setOpenAtt] = useState({});
  const [uploadingAid, setUploadingAid] = useState(null);
  const celebrateTimer = useRef(null);

  const fireCelebration = useCallback(() => {
    setCelebrate(true);
    clearTimeout(celebrateTimer.current);
    celebrateTimer.current = setTimeout(() => setCelebrate(false), 950);
  }, []);
  useEffect(() => () => clearTimeout(celebrateTimer.current), []);
  // Les données serveur font foi : on purge les surcharges optimistes à chaque refetch.
  useEffect(() => { setKpiOverride({}); }, [q.data]);

  const run = useCallback(async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); await q.refetch(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error("Action impossible pour le moment"); }
    finally { setBusy(false); }
  }, [q]);

  // Mutations
  const patchAction = (aid, patch, celebrateOnDeploy) => run(async () => {
    await api.patch(`/strategy/actions/${aid}`, patch);
    if (celebrateOnDeploy && patch.statut === 'deploye') fireCelebration();
  });
  const addAction = (wid) => run(async () => { await api.post(`/strategy/workstreams/${wid}/actions`, {}); }, 'Action ajoutée');
  const delAction = (aid) => run(async () => { await api.delete(`/strategy/actions/${aid}`); }, 'Action supprimée');

  // Éditions annuelles (navigation par année)
  const createYear = (annee, resetStatus = false) => run(async () => {
    const { data } = await api.post('/strategy/editions/duplicate', { source_plan_id: curPid, annee, reset_status: resetStatus });
    await qc.invalidateQueries({ queryKey: ['/strategy/plan'] });
    if (data?.id) setEditionId(data.id);
    setNewYear(null);
  }, `Année ${annee} créée`);
  const deleteYear = () => run(async () => {
    // Bascule vers une édition restante concrète (jamais la clé "par défaut" en cache).
    const eds = d?.editions || [];
    const idx = eds.findIndex((e) => e.id === curPid);
    const fallback = idx > 0 ? eds[idx - 1].id : (eds[idx + 1]?.id || null);
    await api.delete(`/strategy/editions/${curPid}`);
    setDeleteYearOpen(false);
    await qc.invalidateQueries({ queryKey: ['/strategy/plan'] });
    setEditionId(fallback);
  }, 'Année supprimée');
  const openCompare = async () => {
    try {
      const { data } = await api.get('/strategy/compare');
      setCompareData(data);
      setCompareOpen(true);
    } catch { toast.error('Comparatif indisponible pour le moment'); }
  };

  // Preuves factuelles (fichiers)
  const uploadAttachment = async (aid, file) => {
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) { toast.error('Fichier trop volumineux (max 10 Mo)'); return; }
    setUploadingAid(aid);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/strategy/actions/${aid}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await q.refetch();
      setOpenAtt((s) => ({ ...s, [aid]: true }));
      toast.success('Preuve ajoutée');
    } catch (e) {
      toast.error(e?.response?.status === 413 ? 'Fichier trop volumineux (max 10 Mo)' : "Envoi impossible pour le moment");
    } finally { setUploadingAid(null); }
  };
  const deleteAttachment = (attId) => run(async () => { await api.delete(`/strategy/attachments/${attId}`); }, 'Preuve supprimée');
  const downloadAttachment = async (att) => {
    try {
      const { data } = await api.get(`/strategy/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: att.content_type || 'application/octet-stream' }));
      const a = document.createElement('a'); a.href = url; a.download = att.filename || 'preuve';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Téléchargement impossible'); }
  };

  const patchWs = (wid, patch) => run(async () => { await api.patch(`/strategy/workstreams/${wid}`, patch); });
  const addWs = () => run(async () => { await api.post(`/strategy/workstreams${qp}`, {}); }, 'Chantier ajouté');
  const delWs = (wid) => run(async () => { await api.delete(`/strategy/workstreams/${wid}`); }, 'Chantier supprimé');
  const patchMeta = (patch) => run(async () => { await api.patch(`/strategy/meta${qp}`, patch); });
  const patchKpi = (kid, patch) => {
    setKpiOverride((s) => ({ ...s, [kid]: { ...(s[kid] || {}), ...patch } }));
    return run(async () => { await api.patch(`/strategy/kpis/${kid}`, patch); });
  };
  const addKpi = () => run(async () => { await api.post(`/strategy/kpis${qp}`, {}); }, 'Indicateur ajouté');
  const delKpi = (kid) => run(async () => { await api.delete(`/strategy/kpis/${kid}`); }, 'Indicateur supprimé');
  const resetAll = () => run(async () => { await api.post(`/strategy/reset${qp}`); }, 'Plan réinitialisé');
  const exportCsv = async () => {
    try {
      const { data } = await api.get('/strategy/export.csv', { responseType: 'blob', params: curPid ? { plan_id: curPid } : undefined });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'Plan_Strategique_Shariti.csv';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export indisponible'); }
  };

  const responsables = useMemo(() => {
    const set = new Set();
    (d?.workstreams || []).forEach((w) => (w.actions || []).forEach((a) => a.responsable && set.add(a.responsable)));
    return Array.from(set).sort();
  }, [d]);

  const matchAction = useCallback((a) => {
    if (fStatut !== 'all' && a.statut !== fStatut) return false;
    if (fResp !== 'all' && a.responsable !== fResp) return false;
    if (fRetard && !a.is_overdue) return false;
    if (fPreuve !== 'all') {
      const hasProof = (a.attachments && a.attachments.length > 0) || !!(a.preuve_note && a.preuve_note.trim());
      if (fPreuve === 'with' && !hasProof) return false;
      if (fPreuve === 'without' && hasProof) return false;
    }
    if (search && !(`${a.titre} ${a.responsable}`.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  }, [fStatut, fResp, fPreuve, fRetard, search]);

  if (!d) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="plan-strategique-loading">
        {q.isError
          ? <Card className="p-8 text-center text-sm text-muted-foreground">Impossible de charger le plan stratégique.</Card>
          : <div className="space-y-6"><Skeleton className="h-56 rounded-3xl" /><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-96 rounded-2xl" /></div>}
      </div>
    );
  }

  const meta = d.meta || {};
  const g = d.gamification || {};
  const quarters = d.quarters || [];
  const workstreams = d.workstreams || [];
  const kpis = (d.kpis || []).map((k) => (kpiOverride[k.id] ? { ...k, ...kpiOverride[k.id] } : k));
  const matrice = meta.matrice || {};
  const wsByNum = Object.fromEntries(workstreams.map((w) => [w.numero, w]));

  // Navigation par année (éditions)
  const editions = d.editions || [];
  const edition = d.edition || null;
  const curIdx = editions.findIndex((e) => e.id === edition?.id);
  const prevEd = curIdx > 0 ? editions[curIdx - 1] : null;
  const nextEd = curIdx >= 0 && curIdx < editions.length - 1 ? editions[curIdx + 1] : null;
  const minAnnee = editions.length ? editions[0].annee : (edition?.annee || 0);
  const maxAnnee = editions.length ? editions[editions.length - 1].annee : (edition?.annee || 0);
  const goPrev = () => { if (prevEd) setEditionId(prevEd.id); else setNewYear(minAnnee - 1); };
  const goNext = () => { if (nextEd) setEditionId(nextEd.id); else setNewYear(maxAnnee + 1); };

  return (
    <TooltipProvider>
      <Confetti show={celebrate} />
      <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:space-y-8 sm:px-6 lg:px-8" data-testid="plan-strategique-page">

        {/* ============ BARRE D'ACTIONS + NAVIGATION ANNÉE ============ */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Navigateur d'années */}
          <div className="flex items-center gap-1.5 rounded-2xl border bg-card p-1.5 shadow-sm" data-testid="plan-year-nav">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={goPrev} disabled={busy} data-testid="plan-year-prev" aria-label="Année précédente">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{prevEd ? `Aller à ${prevEd.annee}` : `Créer l'année ${minAnnee - 1}`}</TooltipContent>
            </Tooltip>

            <Select value={edition?.id || ''} onValueChange={(v) => setEditionId(v)}>
              <SelectTrigger className="h-9 min-w-[128px] gap-2 rounded-xl border-0 bg-transparent font-display text-lg font-bold shadow-none focus:ring-0" data-testid="plan-year-select">
                <CalendarDays className="h-4 w-4 accent-text" />
                <SelectValue>{edition?.annee || '—'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {editions.map((e) => (
                  <SelectItem key={e.id} value={e.id} className="text-sm font-semibold" data-testid={`plan-year-opt-${e.annee}`}>Année {e.annee}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={goNext} disabled={busy} data-testid="plan-year-next" aria-label="Année suivante">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{nextEd ? `Aller à ${nextEd.annee}` : `Créer l'année ${maxAnnee + 1}`}</TooltipContent>
            </Tooltip>
          </div>

          {/* Actions année */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-xl text-xs" onClick={openCompare} disabled={busy} data-testid="plan-compare-btn">
              <GitCompareArrows className="h-3.5 w-3.5" /> Comparatif annuel
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-xl text-xs" onClick={() => setNewYear(maxAnnee + 1)} disabled={busy} data-testid="plan-year-add">
                  <Plus className="h-3.5 w-3.5" /> Année {maxAnnee + 1}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Créer une nouvelle année</TooltipContent>
            </Tooltip>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-xl text-xs text-rose-500 hover:text-rose-600" onClick={() => setDeleteYearOpen(true)} disabled={busy || editions.length <= 1} data-testid="plan-year-delete-btn">
              <Trash2 className="h-3.5 w-3.5" /> Supprimer l'année
            </Button>
          </div>
        </div>

        {/* Dialog : créer une nouvelle année (copie à l'identique OU repartir de zéro) */}
        <Dialog open={newYear != null} onOpenChange={(o) => { if (!o) setNewYear(null); }}>
          <DialogContent data-testid="plan-newyear-dialog">
            <DialogHeader>
              <DialogTitle>Créer l'année {newYear}</DialogTitle>
              <DialogDescription>Comment souhaitez-vous démarrer l'année {newYear} ? (copie de l'année {edition?.annee})</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => createYear(newYear, false)} disabled={busy}
                className="flex flex-col items-start gap-1.5 rounded-2xl border p-4 text-left transition-colors hover:border-[color:var(--tenant-accent-500)] hover:accent-bg-soft disabled:opacity-60"
                data-testid="plan-newyear-copy">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl accent-bg-100 accent-text"><Copy className="h-4.5 w-4.5" /></span>
                <span className="font-display text-sm font-semibold">Copier l'état actuel</span>
                <span className="text-xs text-muted-foreground">Reprend les chantiers, actions et leurs statuts à l'identique — idéal pour continuer sur la lancée.</span>
              </button>
              <button type="button" onClick={() => createYear(newYear, true)} disabled={busy}
                className="flex flex-col items-start gap-1.5 rounded-2xl border p-4 text-left transition-colors hover:border-[color:var(--tenant-accent-500)] hover:accent-bg-soft disabled:opacity-60"
                data-testid="plan-newyear-scratch">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground"><Eraser className="h-4.5 w-4.5" /></span>
                <span className="font-display text-sm font-semibold">Repartir de zéro</span>
                <span className="text-xs text-muted-foreground">Copie la même structure mais remet toutes les actions à « à lancer » — nouveau suivi annuel.</span>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog : comparatif annuel */}
        <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
          <DialogContent className="max-w-3xl" data-testid="plan-compare-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><GitCompareArrows className="h-4 w-4 accent-text" /> Comparatif annuel</DialogTitle>
              <DialogDescription>Progression du plan d'une année à l'autre — global et par chantier.</DialogDescription>
            </DialogHeader>
            {compareData && (() => {
              const years = compareData.years || [];
              const cols = `minmax(150px,1.4fr) ${years.map(() => 'minmax(72px,1fr)').join(' ')}`;
              const gByYear = Object.fromEntries((compareData.global || []).map((g) => [g.annee, g]));
              return (
                <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
                  {/* Progression globale */}
                  <div className="rounded-2xl border p-3">
                    <div className="grid items-center gap-2" style={{ gridTemplateColumns: cols }}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Progression globale</div>
                      {years.map((y) => {
                        const g = gByYear[y] || {};
                        return (
                          <div key={y} className="text-center" data-testid={`compare-global-${y}`}>
                            <div className="font-display text-xl font-bold tabular accent-text">{g.progress ?? 0}%</div>
                            <div className="text-[10px] text-muted-foreground">{y}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: cols }}>
                      <div className="text-[10px] text-muted-foreground">Actions déployées · retards</div>
                      {years.map((y) => {
                        const g = gByYear[y] || {};
                        return (
                          <div key={y} className="text-center text-[10px] text-muted-foreground">
                            {g.actions_deployees ?? 0}/{g.actions_total ?? 0}
                            {g.actions_en_retard ? <span className="ml-1 text-rose-500">· {g.actions_en_retard}⚠</span> : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Par chantier */}
                  <div className="rounded-2xl border">
                    <div className="grid items-center gap-2 border-b bg-muted/40 px-3 py-2" style={{ gridTemplateColumns: cols }}>
                      <div className="text-[11px] font-semibold text-muted-foreground">Chantier</div>
                      {years.map((y) => <div key={y} className="text-center text-[11px] font-semibold text-muted-foreground">{y}</div>)}
                    </div>
                    {(compareData.chantiers || []).map((c) => (
                      <div key={c.numero} className="grid items-center gap-2 border-b px-3 py-2 last:border-b-0" style={{ gridTemplateColumns: cols }} data-testid={`compare-ws-${c.numero}`}>
                        <div className="flex items-center gap-1.5 truncate text-xs font-medium">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: wsColor(c.couleur || c.numero) }} />
                          <span className="truncate">{c.numero}. {c.titre}</span>
                        </div>
                        {years.map((y) => {
                          const cell = (c.by_year || {})[String(y)];
                          const pct = cell ? cell.progress : null;
                          return (
                            <div key={y} className="flex flex-col items-center gap-1">
                              {pct === null ? <span className="text-[10px] text-muted-foreground">—</span> : (
                                <>
                                  <span className="text-xs font-semibold tabular" style={{ color: wsColor(c.couleur || c.numero) }}>{pct}%</span>
                                  <div className="h-1.5 w-full max-w-[64px] overflow-hidden rounded-full bg-muted">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: wsColor(c.couleur || c.numero) }} />
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Dialog : suppression d'une année */}
        <AlertDialog open={deleteYearOpen} onOpenChange={setDeleteYearOpen}>
          <AlertDialogContent data-testid="plan-year-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l'année {edition?.annee} ?</AlertDialogTitle>
              <AlertDialogDescription>
                Tout le contenu de l'année {edition?.annee} (chantiers, actions, KPIs et preuves) sera définitivement supprimé. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={deleteYear} className="bg-rose-600 hover:bg-rose-700" data-testid="plan-year-delete-confirm">Supprimer définitivement</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden rounded-3xl border border-white/10 exec-hero-bg" data-testid="plan-hero">
          <div className="exec-grain pointer-events-none absolute inset-0 opacity-[0.12]" />
          <div className="relative grid grid-cols-1 gap-6 p-6 sm:p-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ background: 'var(--tenant-gradient)' }}>
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <EditableText value={meta.org_name} onSave={(v) => patchMeta({ org_name: v })} placeholder="Nom" className="font-display text-lg font-bold text-white" inputClassName="h-7 bg-white/90 text-sm text-foreground" testid="plan-org-name" />
                  <EditableText value={meta.org_tagline} onSave={(v) => patchMeta({ org_tagline: v })} placeholder="Signature" className="text-[11px] text-white/60" inputClassName="h-7 bg-white/90 text-xs text-foreground" testid="plan-org-tagline" />
                </div>
              </div>
              <EditableText value={meta.kicker} onSave={(v) => patchMeta({ kicker: v })} placeholder="Sur-titre" className="mt-6 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50" inputClassName="h-7 bg-white/90 text-xs text-foreground" testid="plan-kicker" />
              <EditableText value={meta.titre} onSave={(v) => patchMeta({ titre: v })} placeholder="Titre du plan" className="mt-1 font-display text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl" inputClassName="bg-white/90 text-2xl font-bold text-foreground" testid="plan-title" />
              <EditableText value={meta.sous_titre} onSave={(v) => patchMeta({ sous_titre: v })} multiline placeholder="Sous-titre" className="mt-3 max-w-xl text-sm leading-relaxed text-white/70" inputClassName="mt-3 min-h-[60px] bg-white/90 text-sm text-foreground" testid="plan-soustitre" />
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm">
                <div className="flex items-center gap-2 text-white">
                  <ClipboardCheck className="h-4 w-4 shrink-0 text-[color:var(--tenant-accent-bright)]" />
                  <EditableText value={meta.priorites_label} onSave={(v) => patchMeta({ priorites_label: v })} placeholder="Titre du bloc priorités" className="font-display text-xs font-semibold uppercase tracking-wide text-white" inputClassName="h-7 bg-white/90 text-xs text-foreground" testid="plan-priorites-label" />
                </div>
                <ol className="mt-3 space-y-2">
                  {(meta.priorites || []).map((p, i) => {
                    const arr = meta.priorites || [];
                    const moveP = (dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return; const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; patchMeta({ priorites: c }); };
                    return (
                      <li key={i} className="group/prio flex items-start gap-2.5" data-testid={`plan-priorite-${i}`}>
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--tenant-accent-500)] text-[10px] font-bold text-white">{i + 1}</span>
                        <EditableText value={p} onSave={(v) => { const c = [...arr]; c[i] = v; patchMeta({ priorites: c }); }}
                          className="flex-1 text-[13px] leading-snug text-white/90" inputClassName="h-7 bg-white/90 text-sm text-foreground" testid={`plan-priorite-edit-${i}`} />
                        <div className="mt-0.5 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/prio:opacity-100">
                          <button type="button" disabled={i === 0} onClick={() => moveP(-1)} aria-label="Monter" className="rounded p-0.5 text-white/60 enabled:hover:text-white disabled:opacity-20"><ChevronDown className="h-3 w-3 rotate-180" /></button>
                          <button type="button" disabled={i === arr.length - 1} onClick={() => moveP(1)} aria-label="Descendre" className="rounded p-0.5 text-white/60 enabled:hover:text-white disabled:opacity-20"><ChevronDown className="h-3 w-3" /></button>
                          <button type="button" onClick={() => patchMeta({ priorites: arr.filter((_, j) => j !== i) })} aria-label="Supprimer" className="rounded p-0.5 text-rose-300 hover:text-rose-200"><X className="h-3 w-3" /></button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <button type="button" onClick={() => patchMeta({ priorites: [...(meta.priorites || []), 'Nouvelle priorité'] })} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-white/70 transition-colors hover:text-white" data-testid="plan-priorite-add"><Plus className="h-3 w-3" /> Ajouter une priorité</button>
              </div>
            </div>
          </div>
        </section>

        {/* ============ OBJECTIF / MISSION / VALEURS / AMBITION ============ */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            { icon: Target, k: 'objectif', label: 'Notre objectif' },
            { icon: Zap, k: 'mission', label: 'Notre mission' },
            { icon: Gem, k: 'valeurs', label: 'Nos valeurs' },
            { icon: Award, k: 'ambition', label: 'Notre ambition' },
          ].map((c) => (
            <Card key={c.k} className="p-4" data-testid={`plan-meta-${c.k}`}>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[color:var(--tenant-accent-100)] text-[color:var(--tenant-accent-600)]"><c.icon className="h-4 w-4" /></div>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</span>
              </div>
              {c.k === 'valeurs' ? (
                <EditableValues values={meta.valeurs} onChange={(v) => patchMeta({ valeurs: v })} testid="plan-valeurs" />
              ) : (
                <div className="mt-2">
                  <EditableText value={meta[c.k]} onSave={(v) => patchMeta({ [c.k]: v })} multiline className="text-sm leading-relaxed text-foreground/90" inputClassName="text-sm" testid={`plan-meta-${c.k}-text`} />
                </div>
              )}
            </Card>
          ))}
        </section>

        {/* ============ VUE EXÉCUTIVE & PILOTAGE (indicateurs clés) ============ */}
        <section className="space-y-3" data-testid="plan-exec-view">
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-2"><Gauge className="h-5 w-5 accent-text" /><h2 className="font-display text-xl font-bold">Vue exécutive & pilotage</h2></div>
            <span className="text-right text-[11px] uppercase tracking-wide text-muted-foreground">Indicateurs clés — à mettre à jour chaque mois</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="plan-kpis">
            {kpis.map((k) => {
              const linked = workstreams.find((w) => w.id === k.workstream_id);
              return (
                <Card key={k.id} className="group/kpi flex flex-col p-4" data-testid={`kpi-${k.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <EditableText value={k.label} onSave={(v) => patchKpi(k.id, { label: v })} inputClassName="h-7 text-xs" testid={`kpi-${k.id}-label`} />
                    </div>
                    <button type="button" onClick={() => delKpi(k.id)} aria-label="Supprimer" className="shrink-0 opacity-0 transition-opacity group-hover/kpi:opacity-100"><X className="h-3.5 w-3.5 text-rose-500" /></button>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <div className="font-display text-3xl font-bold tabular" style={{ color: 'var(--tenant-accent-600)' }}>
                      <EditableText value={k.cible} onSave={(v) => patchKpi(k.id, { cible: v })} inputClassName="h-9 w-24 text-2xl" testid={`kpi-${k.id}-cible`} />
                    </div>
                    {k.sens === 'baisse' ? <TrendingDown className="h-4 w-4 text-emerald-600" /> : <TrendingUp className="h-4 w-4 text-emerald-600" />}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{k.sous_titre}</div>

                  {/* Valeur actuelle (saisie manuelle OU auto depuis un rapport d'attrition) */}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-muted-foreground">Actuel :</span>
                    {k.attrition_measure ? (
                      <span className="flex items-center gap-1 font-semibold text-foreground" data-testid={`kpi-${k.id}-valeur-auto`}>
                        {k.valeur_actuelle || '—'}
                        {k.attrition_value?.report_date && (
                          <Badge variant="outline" className="h-4 gap-0.5 px-1 text-[9px]"><HeartPulse className="h-2.5 w-2.5" /> {k.attrition_value.report_date}</Badge>
                        )}
                      </span>
                    ) : (
                      <span className="font-semibold text-foreground">
                        <EditableText value={k.valeur_actuelle} onSave={(v) => patchKpi(k.id, { valeur_actuelle: v })} placeholder="—" inputClassName="h-6 w-24 text-[11px]" testid={`kpi-${k.id}-valeur`} />
                      </span>
                    )}
                  </div>
                  {attrOptions.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="shrink-0 text-[10px] text-muted-foreground">Source auto :</span>
                      <Select value={k.attrition_measure || 'none'} onValueChange={(v) => patchKpi(k.id, { attrition_measure: v === 'none' ? null : v, ...(v === 'none' ? { valeur_actuelle: null } : {}) })}>
                        <SelectTrigger className="h-7 flex-1 rounded-lg text-[11px]" data-testid={`kpi-${k.id}-attr-source`}><SelectValue placeholder="Aucune" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" className="text-xs">Aucune (saisie manuelle)</SelectItem>
                          {attrOptions.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label} · {o.association}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Avancement (manuel ou auto depuis le chantier lié) */}
                  <div className="mt-2">
                    <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1">Avancement {k.progress_source === 'auto' && <Badge variant="outline" className="h-4 gap-0.5 px-1 text-[9px]"><Zap className="h-2.5 w-2.5" /> auto</Badge>}</span>
                      <span className="flex items-center gap-1">
                        {k.progress_source === 'auto'
                          ? <span className="font-semibold text-foreground">{k.progress || 0}%</span>
                          : <><EditableText value={String(k.progress ?? 0)} onSave={(v) => patchKpi(k.id, { progress: Math.max(0, Math.min(100, parseInt(v, 10) || 0)) })} inputClassName="h-6 w-12 text-[10px]" testid={`kpi-${k.id}-progress`} />%</>}
                      </span>
                    </div>
                    <Progress value={k.progress || 0} className="h-2" />
                  </div>

                  {/* Réalisations / détails (ex. clients signés) */}
                  <div className="mt-2 rounded-md bg-muted/40 p-2">
                    <div className="mb-0.5 flex items-center gap-1 text-[10px] font-medium text-muted-foreground"><StickyNote className="h-3 w-3" /> Réalisations / détails</div>
                    <EditableText value={k.note} onSave={(v) => patchKpi(k.id, { note: v })} multiline placeholder="Ex. clients signés cette année, chiffres, précisions…" className="text-[11px] leading-relaxed" inputClassName="min-h-[44px] text-[11px]" testid={`kpi-${k.id}-note`} />
                  </div>

                  {/* Lien vers un chantier stratégique */}
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="shrink-0 text-[10px] text-muted-foreground">Chantier lié :</span>
                    <Select value={k.workstream_id || 'none'} onValueChange={(v) => patchKpi(k.id, { workstream_id: v === 'none' ? null : v, ...(v === 'none' ? { auto_progress: false } : {}) })}>
                      <SelectTrigger className="h-7 flex-1 rounded-lg text-[11px]" data-testid={`kpi-${k.id}-lien`}><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs">Aucun</SelectItem>
                        {workstreams.map((w) => <SelectItem key={w.id} value={w.id} className="text-xs">{w.numero}. {w.titre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {linked && (
                    <label className="mt-1.5 flex cursor-pointer items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1" data-testid={`kpi-${k.id}-auto-row`}>
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Zap className="h-3 w-3" /> Avancement auto depuis « {linked.titre} » ({linked.stats?.progress ?? 0}%)</span>
                      <Switch checked={!!k.auto_progress} onCheckedChange={(v) => patchKpi(k.id, { auto_progress: v })} className="scale-75" data-testid={`kpi-${k.id}-auto`} />
                    </label>
                  )}
                </Card>
              );
            })}
            <button type="button" onClick={addKpi} className="flex min-h-[200px] items-center justify-center rounded-2xl border border-dashed text-sm text-muted-foreground transition-colors hover:border-[color:var(--tenant-accent-500)] hover:text-foreground" data-testid="plan-add-kpi">
              <span className="inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Ajouter un indicateur</span>
            </button>
          </div>
        </section>


        {/* ============ PROGRESSION GAMIFIÉE + BARRE D'OUTILS ============ */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-12" data-testid="plan-progress">
          <Card className="lg:col-span-5">
            <div className="flex flex-col gap-4 p-5">
              <div className="flex items-center gap-5">
                <StatusDonut repartition={g.repartition || {}} total={g.actions_grand_total || 0} deployedPct={g.progress || 0} selected={fStatut} onSelect={(v) => setFStatut(fStatut === v ? 'all' : v)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--tenant-accent-500)] font-display text-sm font-bold text-white" data-testid="plan-level">N{g.niveau}</span>
                    <div>
                      <div className="font-display text-sm font-semibold">Niveau {g.niveau} · {g.xp} XP</div>
                      <div className="text-[11px] text-muted-foreground">{g.actions_deployees}/{g.actions_total} actions déployées</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground"><span>Progression du niveau</span><span className="tabular">{g.xp_dans_niveau}/{g.xp_prochain_niveau} XP</span></div>
                    <Progress value={(g.xp_dans_niveau / (g.xp_prochain_niveau || 100)) * 100} className="h-2" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="gap-1 text-[10px]"><Trophy className="h-3 w-3" /> {g.chantiers_complets}/{g.chantiers_total} chantiers finis</Badge>
                    <Badge variant="outline" className="gap-1 text-[10px]"><CheckCircle2 className="h-3 w-3" /> {g.actions_testees_ou_plus} testées+</Badge>
                    {g.actions_en_retard > 0 && (
                      <button type="button" onClick={() => setFRetard((v) => !v)} title={fRetard ? 'Retirer le filtre en retard' : "N'afficher que les actions en retard"} data-testid="plan-overdue-badge">
                        <Badge variant="outline" className={`cursor-pointer gap-1 border-rose-300 text-[10px] text-rose-600 transition-colors hover:bg-rose-100 ${fRetard ? 'bg-rose-100 ring-1 ring-rose-300' : 'bg-rose-50'}`}><AlertTriangle className="h-3 w-3" /> {g.actions_en_retard} en retard</Badge>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Répartition des actions par statut (cliquer pour filtrer) */}
              {(() => {
                const rep = g.repartition || {};
                const grandTotal = g.actions_grand_total || 0;
                return (
                  <div className="border-t pt-3">
                    <div className="mb-1.5 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      <span>Répartition des actions</span>
                      <span className="tabular">{grandTotal} au total</span>
                    </div>
                    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" data-testid="plan-status-bar">
                      {STATUTS.map((s) => {
                        const n = rep[s.value] || 0;
                        const w = grandTotal ? (n / grandTotal) * 100 : 0;
                        if (!w) return null;
                        return <div key={s.value} title={`${s.label} : ${n}`} style={{ width: `${w}%`, background: s.color }} className="h-full transition-[width] duration-300" />;
                      })}
                    </div>
                    <div className="mt-2.5 grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-3">
                      {STATUTS.map((s) => {
                        const active = fStatut === s.value;
                        return (
                          <button key={s.value} type="button" onClick={() => setFStatut(active ? 'all' : s.value)}
                            title={active ? 'Retirer le filtre' : `Filtrer : ${s.label}`}
                            className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[11px] transition-colors hover:bg-muted ${active ? 'bg-muted ring-1 ring-[color:var(--tenant-accent-300)]' : ''}`}
                            data-testid={`plan-rep-${s.value}`}>
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
                            <span className="truncate text-muted-foreground">{s.label}</span>
                            <span className="ml-auto font-semibold tabular text-foreground" data-testid={`plan-rep-count-${s.value}`}>{rep[s.value] || 0}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Card>
          <Card className="lg:col-span-7">
            <div className="flex h-full flex-col gap-3 p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une action…" className="rounded-xl pl-9" data-testid="plan-search" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={fStatut} onValueChange={setFStatut}>
                  <SelectTrigger className="h-9 w-[150px] rounded-xl text-xs" data-testid="plan-filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Tous les statuts</SelectItem>
                    {STATUTS.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fResp} onValueChange={setFResp}>
                  <SelectTrigger className="h-9 w-[170px] rounded-xl text-xs" data-testid="plan-filter-resp"><SelectValue placeholder="Responsable" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Tous les responsables</SelectItem>
                    {responsables.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fPreuve} onValueChange={setFPreuve}>
                  <SelectTrigger className="h-9 w-[150px] rounded-xl text-xs" data-testid="plan-filter-preuve">
                    <span className="flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="Preuve" /></span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Toutes les preuves</SelectItem>
                    <SelectItem value="with" className="text-xs">Avec preuve</SelectItem>
                    <SelectItem value="without" className="text-xs">Sans preuve</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant={fRetard ? 'default' : 'outline'} onClick={() => setFRetard((v) => !v)} aria-pressed={fRetard}
                  className={`h-9 gap-1.5 rounded-xl text-xs ${fRetard ? 'bg-rose-600 text-white hover:bg-rose-700' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
                  data-testid="plan-filter-retard"><AlertTriangle className="h-3.5 w-3.5" /> En retard{g.actions_en_retard ? ` (${g.actions_en_retard})` : ''}</Button>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-xl" onClick={addWs} disabled={busy} data-testid="plan-add-workstream"><Plus className="h-4 w-4" /> Chantier</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-xl" onClick={exportCsv} data-testid="plan-export"><Download className="h-4 w-4" /> Export</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="gap-1.5 rounded-xl text-muted-foreground" data-testid="plan-reset"><RotateCcw className="h-4 w-4" /> Réinit.</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Réinitialiser le plan ?</AlertDialogTitle>
                        <AlertDialogDescription>Toutes vos modifications (statuts, échéances, ajouts) seront remplacées par le plan d'origine. Cette action est irréversible.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAll} className="bg-rose-600 hover:bg-rose-700">Réinitialiser</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-auto flex flex-wrap gap-2 text-[11px]">
                {STATUTS.map((s) => (
                  <span key={s.value} className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} /> {s.label}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        </section>

        {/* ============ CHANTIERS STRATÉGIQUES ============ */}
        <section className="space-y-2">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-xl font-bold">Chantiers stratégiques</h2>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{workstreams.length} chantiers · 12 mois</span>
          </div>
          <div className="space-y-4">
            {workstreams.map((w) => {
              const Icon = WS_ICON[w.numero] || Flag;
              const col = wsColor(w.couleur || w.numero);
              const st = w.stats || {};
              const isOpen = openWs[w.id] === true; // repliés par défaut
              const visibleActions = (w.actions || []).filter(matchAction);
              return (
                <Card key={w.id} className="overflow-hidden" style={{ borderLeft: `4px solid ${col}` }} data-testid={`workstream-${w.numero}`}>
                  {/* Header */}
                  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                    <button type="button" onClick={() => setOpenWs((s) => ({ ...s, [w.id]: !isOpen }))}
                      className="flex flex-1 items-center gap-3 text-left" data-testid={`workstream-${w.numero}-toggle`}>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: col }}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-display text-[15px] font-bold" style={{ color: col }}>{w.numero}.</span>
                          <span className="truncate font-display text-[15px] font-bold">{w.titre}</span>
                          {st.badge && <Badge variant="outline" className={`gap-1 text-[10px] ${BADGE_META[st.badge].cls}`}><Star className="h-3 w-3" /> {BADGE_META[st.badge].label}</Badge>}
                          {st.en_retard > 0 && <Badge variant="outline" className="gap-1 border-rose-300 bg-rose-50 text-[10px] text-rose-600" data-testid={`workstream-${w.numero}-overdue`}><AlertTriangle className="h-3 w-3" /> {st.en_retard} en retard</Badge>}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">{w.sous_titre}</p>
                      </div>
                      <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="flex items-start gap-3 sm:w-72">
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground"><span>{st.deployees || 0}/{st.actives || 0} déployées</span><span className="tabular font-semibold" style={{ color: col }}>{st.progress || 0}%</span></div>
                        <MiniStatusBar repartition={st.repartition || {}} total={st.total || 0} testid={`workstream-${w.numero}-repartition`} />
                      </div>
                      <Badge className="mt-0.5 shrink-0 border-transparent text-[10px] text-white" style={{ background: col }}>{w.priorite}</Badge>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t">
                      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[minmax(0,220px)_1fr_minmax(0,230px)]">
                        {/* Objectifs clés */}
                        <div>
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Objectifs clés</div>
                          <EditableList items={w.objectifs} onChange={(v) => patchWs(w.id, { objectifs: v })} addLabel="Ajouter un objectif" dotColor={col} testid={`workstream-${w.numero}-objectifs`} />
                          <div className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Priorité · Effort</div>
                          <div className="mt-1.5 flex items-center gap-2">
                            <Select value={w.priorite} onValueChange={(v) => patchWs(w.id, { priorite: v })}>
                              <SelectTrigger className="h-8 w-[74px] rounded-lg text-xs" data-testid={`workstream-${w.numero}-priorite`}><SelectValue /></SelectTrigger>
                              <SelectContent>{PRIORITES.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={w.effort} onValueChange={(v) => patchWs(w.id, { effort: v })}>
                              <SelectTrigger className="h-8 w-[104px] rounded-lg text-xs" data-testid={`workstream-${w.numero}-effort`}><SelectValue /></SelectTrigger>
                              <SelectContent>{EFFORTS.map((e) => <SelectItem key={e.value} value={e.value} className="text-xs">{e.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <EffortDots effort={w.effort} />
                          </div>
                          <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Impact</div>
                          <EditableList items={w.impacts} onChange={(v) => patchWs(w.id, { impacts: v })} addLabel="Ajouter un impact" dotColor={col} testid={`workstream-${w.numero}-impacts`} />
                        </div>

                        {/* Actions (checklist) */}
                        <div className="min-w-0">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actions clés (checklist)</span>
                            <Button size="sm" variant="ghost" className="h-7 gap-1 rounded-lg text-[11px] accent-text" onClick={() => addAction(w.id)} disabled={busy} data-testid={`workstream-${w.numero}-add-action`}><Plus className="h-3.5 w-3.5" /> Action</Button>
                          </div>
                          <div className="space-y-1">
                            {visibleActions.length === 0 && <p className="py-3 text-center text-xs text-muted-foreground">Aucune action (filtre actif ou chantier vide).</p>}
                            {visibleActions.map((a) => {
                              const done = a.statut === 'deploye';
                              const atts = a.attachments || [];
                              const attOpen = !!openAtt[a.id];
                              const overdue = a.is_overdue;
                              const noteText = (a.preuve_note || '').trim();
                              const hasProof = atts.length > 0 || !!noteText;
                              return (
                                <div key={a.id} className="group/act rounded-lg px-1.5 py-1.5 hover:bg-muted/40" data-testid={`action-${a.id}`}>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Checkbox checked={done} onCheckedChange={(v) => patchAction(a.id, { statut: v ? 'deploye' : 'a_lancer' }, true)} className="shrink-0" data-testid={`action-${a.id}-check`} />
                                    <div className="min-w-[160px] flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <EditableText value={a.titre} onSave={(v) => patchAction(a.id, { titre: v })} className={`text-sm ${done ? 'text-muted-foreground line-through' : ''}`} inputClassName="h-7 text-sm" testid={`action-${a.id}-titre`} />
                                        {overdue && (
                                          <Badge variant="outline" className="shrink-0 gap-1 border-rose-300 bg-rose-50 text-[10px] font-medium text-rose-600" data-testid={`action-${a.id}-overdue`}>
                                            <AlertTriangle className="h-3 w-3" /> En retard
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <div className="w-[120px] shrink-0">
                                      <EditableText value={a.responsable} onSave={(v) => patchAction(a.id, { responsable: v })} className="text-xs text-muted-foreground" inputClassName="h-7 text-xs" testid={`action-${a.id}-resp`} />
                                    </div>
                                    <div className="w-[92px] shrink-0">
                                      <EditableText value={a.echeance} onSave={(v) => patchAction(a.id, { echeance: v })} className={`text-xs tabular ${overdue ? 'font-medium text-rose-600' : 'text-muted-foreground'}`} inputClassName="h-7 text-xs" testid={`action-${a.id}-echeance`} />
                                    </div>
                                    <StatusSelect value={a.statut} onChange={(v) => patchAction(a.id, { statut: v }, true)} testid={`action-${a.id}-statut`} />
                                    <button type="button" onClick={() => setOpenAtt((s) => ({ ...s, [a.id]: !attOpen }))}
                                      className={`relative shrink-0 rounded-md p-1 transition-colors hover:bg-muted ${hasProof ? 'text-[color:var(--tenant-accent-600)]' : 'text-muted-foreground opacity-60 group-hover/act:opacity-100'}`}
                                      aria-label="Preuves (note + fichiers)" title="Preuves (note + fichiers)" data-testid={`action-${a.id}-attach-toggle`}>
                                      <Paperclip className="h-3.5 w-3.5" />
                                      {atts.length > 0 && <span className="absolute -right-1 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-[color:var(--tenant-accent-500)] px-0.5 text-[9px] font-bold text-white" data-testid={`action-${a.id}-attach-count`}>{atts.length}</span>}
                                      {atts.length === 0 && noteText && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[color:var(--tenant-accent-500)]" data-testid={`action-${a.id}-note-dot`} />}
                                    </button>
                                    <button type="button" onClick={() => delAction(a.id)} aria-label="Supprimer l'action" className="shrink-0 opacity-0 transition-opacity group-hover/act:opacity-100" data-testid={`action-${a.id}-del`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>
                                  </div>

                                  {/* Horodatage + accès rapide preuves */}
                                  <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-7 text-[10px] text-muted-foreground">
                                    <Clock className="h-3 w-3" />
                                    <span data-testid={`action-${a.id}-created`}>Ajoutée le {fmtDate(a.created_at)}</span>
                                    {!attOpen && noteText && (
                                      <button type="button" onClick={() => setOpenAtt((s) => ({ ...s, [a.id]: true }))} className="ml-1 inline-flex min-w-0 items-center gap-1 accent-text hover:underline" title={noteText} data-testid={`action-${a.id}-note-preview`}>
                                        <Quote className="h-3 w-3 shrink-0" /> <span className="max-w-[220px] truncate">{noteText}</span>
                                      </button>
                                    )}
                                    {atts.length > 0 && !attOpen && (
                                      <button type="button" onClick={() => setOpenAtt((s) => ({ ...s, [a.id]: true }))} className="ml-1 inline-flex items-center gap-1 accent-text hover:underline">
                                        <Paperclip className="h-3 w-3" /> {atts.length} fichier{atts.length > 1 ? 's' : ''}
                                      </button>
                                    )}
                                  </div>

                                  {/* Panneau preuves : note écrite + pièces jointes */}
                                  {attOpen && (
                                    <div className="mt-2 ml-7 rounded-lg border bg-muted/30 p-2.5" data-testid={`action-${a.id}-attach-panel`}>
                                      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                        <ClipboardCheck className="h-3 w-3" /> Preuves de réalisation
                                      </div>
                                      {/* Note écrite (une phrase) */}
                                      <div className="mb-2 rounded-md bg-background p-2">
                                        <div className="mb-1 flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                                          <StickyNote className="h-3 w-3" /> Note / preuve écrite
                                        </div>
                                        <EditableText value={a.preuve_note} onSave={(v) => patchAction(a.id, { preuve_note: v })} multiline placeholder="Décrire en une phrase ce qui a été réalisé (ex. « Deck validé et envoyé au board le 12/03 »)…" className="w-full text-xs leading-relaxed" inputClassName="min-h-[52px] text-xs" testid={`action-${a.id}-note`} />
                                      </div>
                                      {/* Pièces jointes (fichiers) */}
                                      <div className="mb-1 flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                                        <Paperclip className="h-3 w-3" /> Pièces jointes
                                      </div>
                                      <div className="space-y-1">
                                        {atts.length === 0 && <p className="text-[11px] italic text-muted-foreground">Aucun fichier — facultatif (PDF, image, etc.).</p>}
                                        {atts.map((att) => (
                                          <div key={att.id} className="group/att flex items-center gap-2 rounded-md bg-background px-2 py-1.5" data-testid={`attachment-${att.id}`}>
                                            <FileText className="h-3.5 w-3.5 shrink-0 text-[color:var(--tenant-accent-600)]" />
                                            <button type="button" onClick={() => downloadAttachment(att)} className="min-w-0 flex-1 truncate text-left text-xs font-medium hover:underline" title={att.filename} data-testid={`attachment-${att.id}-download`}>{att.filename}</button>
                                            <span className="shrink-0 tabular text-[10px] text-muted-foreground">{fmtBytes(att.size)}</span>
                                            <button type="button" onClick={() => downloadAttachment(att)} className="shrink-0 text-muted-foreground transition-colors hover:text-foreground" aria-label="Télécharger" title="Télécharger"><Download className="h-3.5 w-3.5" /></button>
                                            <button type="button" onClick={() => deleteAttachment(att.id)} className="shrink-0 text-rose-500 opacity-0 transition-opacity hover:text-rose-600 group-hover/att:opacity-100" aria-label="Supprimer la preuve" title="Supprimer" data-testid={`attachment-${att.id}-del`}><Trash2 className="h-3.5 w-3.5" /></button>
                                          </div>
                                        ))}
                                      </div>
                                      <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[color:var(--tenant-accent-400)] hover:text-foreground" data-testid={`action-${a.id}-upload`}>
                                        {uploadingAid === a.id
                                          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi…</>
                                          : <><Upload className="h-3.5 w-3.5" /> Joindre un fichier</>}
                                        <input type="file" className="hidden" disabled={uploadingAid === a.id}
                                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadAttachment(a.id, f); }} />
                                      </label>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Résultats / retour board */}
                        <div>
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Résultats / retour board</div>
                          <Card className="bg-muted/30 p-2">
                            <EditableText value={w.retour_board} onSave={(v) => patchWs(w.id, { retour_board: v })} placeholder="Ajouter un retour du board…" multiline className="min-h-[80px] w-full text-xs leading-relaxed" inputClassName="min-h-[80px] text-xs" testid={`workstream-${w.numero}-board`} />
                          </Card>
                          <div className="mt-3 flex justify-end">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 gap-1 rounded-lg text-[11px] text-rose-500 hover:text-rose-600" data-testid={`workstream-${w.numero}-del`}><Trash2 className="h-3.5 w-3.5" /> Supprimer le chantier</Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer « {w.titre} » ?</AlertDialogTitle>
                                  <AlertDialogDescription>Le chantier et ses {(w.actions || []).length} actions seront définitivement supprimés.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => delWs(w.id)} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>

        {/* ============ ROADMAP & RÈGLES DE PILOTAGE ============ */}
        <section className="space-y-3">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-xl font-bold">Roadmap & règles de pilotage</h2>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">À revoir chaque mois</span>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {/* Roadmap timeline */}
            <Card className="p-4 lg:col-span-8" data-testid="plan-roadmap">
              <div className="mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 accent-text" /><span className="font-display text-sm font-semibold">Roadmap</span></div>
              <div className="overflow-x-auto">
                <div className="min-w-[540px]">
                  {/* En-tête trimestres */}
                  <div className="mb-2 flex items-center">
                    <div className="w-[150px] shrink-0" />
                    <div className="flex flex-1">
                      {quarters.map((qt) => <div key={qt} className="flex-1 text-center text-[10px] font-medium text-muted-foreground">{qt}</div>)}
                    </div>
                  </div>
                  {/* Lignes chantiers */}
                  <div className="space-y-2">
                    {workstreams.map((w) => {
                      const col = wsColor(w.couleur || w.numero);
                      const Q = Math.max(1, quarters.length);
                      const rm = w.roadmap || { start: 0, end: 0 };
                      const s = Math.max(0, Math.min(Q - 1, Math.round(rm.start || 0)));
                      const e = Math.max(s, Math.min(Q - 1, Math.round(rm.end ?? s)));
                      const left = (s / Q) * 100;
                      const width = ((e - s + 1) / Q) * 100;
                      return (
                        <div key={w.id} className="flex items-center" data-testid={`roadmap-${w.numero}`}>
                          <div className="flex w-[150px] shrink-0 items-center gap-1.5 pr-2">
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ background: col }}>{w.numero}</span>
                            <span className="truncate text-[11px] font-medium" title={w.titre}>{w.titre}</span>
                          </div>
                          <div className="relative h-6 flex-1 rounded-md bg-muted/40">
                            {quarters.map((_, i) => (i > 0 ? <div key={i} className="absolute top-0 h-full border-l border-dashed border-border/50" style={{ left: `${(i / Q) * 100}%` }} /> : null))}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="absolute top-0 flex h-6 items-center overflow-hidden rounded-md px-2 text-[10px] font-semibold text-white shadow-sm" style={{ left: `${left}%`, width: `${width}%`, background: col }}>
                                  <span className="truncate">{rm.label || w.titre}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{w.numero}. {w.titre}{rm.note ? ` · ${rm.note}` : ''}</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            {/* Règles + légende */}
            <div className="space-y-4 lg:col-span-4">
              <Card className="p-4" data-testid="plan-regles">
                <div className="mb-2 flex items-center gap-2"><Compass className="h-4 w-4 accent-text" /><span className="font-display text-sm font-semibold">Règles de pilotage</span></div>
                <ol className="space-y-2">
                  {(meta.regles || []).map((r, i) => (
                    <li key={i} className="group/rule flex items-start gap-2">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--tenant-accent-100)] text-[10px] font-bold text-[color:var(--tenant-accent-600)]">{i + 1}</span>
                      <EditableText value={r} onSave={(v) => { const c = [...(meta.regles || [])]; c[i] = v; patchMeta({ regles: c }); }} className="flex-1 text-[13px] leading-snug" inputClassName="h-7 text-sm" testid={`plan-regle-${i}`} />
                      <button type="button" onClick={() => patchMeta({ regles: (meta.regles || []).filter((_, j) => j !== i) })} aria-label="Supprimer" className="opacity-0 group-hover/rule:opacity-100"><X className="h-3 w-3 text-rose-500" /></button>
                    </li>
                  ))}
                </ol>
                <button type="button" onClick={() => patchMeta({ regles: [...(meta.regles || []), 'Nouvelle règle'] })} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium accent-text"><Plus className="h-3 w-3" /> Ajouter une règle</button>
              </Card>
              <Card className="p-4" data-testid="plan-legende">
                <div className="mb-2 flex items-center gap-2"><ListChecks className="h-4 w-4 accent-text" /><span className="font-display text-sm font-semibold">Légende statut</span></div>
                <div className="space-y-1.5">
                  {STATUTS.map((s) => (
                    <div key={s.value} className="flex items-center gap-2 text-xs"><span className="h-3 w-3 rounded-full" style={{ background: s.color }} /> {s.label}</div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* ============ MATRICE IMPACT / EFFORT ============ */}
        <section className="space-y-3">
          <div className="flex items-center gap-2"><Gauge className="h-5 w-5 accent-text" /><h2 className="font-display text-xl font-bold">Matrice Impact / Effort</h2></div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="plan-matrice">
            {[
              { key: 'quick_wins', label: 'Quick wins', sub: 'Fort impact · faible effort', cls: 'border-emerald-200 bg-emerald-50/50', free: true },
              { key: 'projets_majeurs', label: 'Projets majeurs', sub: 'Fort impact · fort effort', cls: 'border-blue-200 bg-blue-50/50', free: false },
              { key: 'faible_roi', label: 'Faible ROI', sub: 'Faible impact · faible effort', cls: 'border-slate-200 bg-slate-50/60', free: true },
              { key: 'a_planifier', label: 'À planifier', sub: 'Faible impact · fort effort', cls: 'border-amber-200 bg-amber-50/50', free: false },
            ].map((quad) => {
              const items = matrice[quad.key] || [];
              return (
                <Card key={quad.key} className={`p-4 ${quad.cls}`} data-testid={`matrice-${quad.key}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{quad.label}</div>
                  <div className="text-[10px] text-muted-foreground">{quad.sub}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {items.length === 0 && <span className="text-xs italic text-muted-foreground">Aucune action identifiée</span>}
                    {quad.free
                      ? items.map((it, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs shadow-sm">{it}</span>
                      ))
                      : items.map((num) => {
                        const w = wsByNum[num];
                        if (!w) return null;
                        const col = wsColor(w.couleur || num);
                        return (
                          <span key={num} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white" style={{ background: col }} data-testid={`matrice-chip-${num}`}>
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/25 text-[9px] font-bold">{num}</span>
                            {w.titre}
                          </span>
                        );
                      })}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        <p className="pb-4 text-center text-[11px] text-muted-foreground" data-testid="plan-footer">
          <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Espace strictement confidentiel — réservé à l'administration SHARITI.
        </p>
      </div>
    </TooltipProvider>
  );
}
